import * as React from 'react'
import * as XLSX from 'xlsx'
import { Box, Button, Checkbox, Chip, Container, Divider, FormControlLabel, Grid2, Paper, Stack, TextField, Typography } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import BusinessCenterRoundedIcon from '@mui/icons-material/BusinessCenterRounded'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import { useDispatch, useSelector } from 'react-redux'
import { addNewParty, getAllParties, updateParty, importParties, getPartyCategories } from '../../features/GeneralManagement/partiesInformationSlice'
import Navbar from '../../components/setup/Navbar'
import AutoComplete from '../../BuiltInComponent/AutoComplete'
import StickyHeadTable from '../../BuiltInComponent/TableWithPagination'

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: 2 } }

export default function PartiesInformation() {
    const dispatch = useDispatch()
    const { parties, partyCategories } = useSelector((state) => state.partyInf)
    const [cateCode, setCateCode] = React.useState('')
    const [openingStatus, setOpeningStatus] = React.useState(false)
    const [editingParty, setEditingParty] = React.useState(null)
    const inputRef = React.useRef()
    const excelInputRef = React.useRef()
    const [importing, setImporting] = React.useState(false)

    React.useEffect(() => {
        dispatch(getAllParties())
        dispatch(getPartyCategories())
    }, [dispatch])

    const resetForm = (formElement) => {
        if (formElement) formElement.reset()
        setEditingParty(null)
        setCateCode('')
        setOpeningStatus(false)
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        const data = new FormData(event.currentTarget)
        const partyName = String(data.get('PartyName') || '').trim()
        if (!cateCode) return window.alert('Please select a Party Category')
        if (!partyName) return window.alert('Party Name is required')
        const partyData = {
            PartyCategory: cateCode, PartyName: partyName,
            PartyUrduName: data.get('PartyUrduName'), email: data.get('email'), ContactNo: data.get('ContactNo'),
            AltContactNo: data.get('AltContactNo'), PartyAddress: data.get('PartyAddress'),
            PartyUrduAddress: data.get('PartyUrduAddress'), Debit: openingStatus ? data.get('Debit') : 0,
            Credit: openingStatus ? data.get('Credit') : 0, PartyStatus: true, OpenStatus: openingStatus,
            ContactPerson: data.get('ContactPerson'), NTN: data.get('NTN'), GST: data.get('GST')
        }
        try {
            if (editingParty) await dispatch(updateParty({ ...partyData, PartyCode: editingParty.PartyCode })).unwrap()
            else await dispatch(addNewParty(partyData)).unwrap()
            await dispatch(getAllParties())
            resetForm(event.currentTarget)
            window.setTimeout(() => inputRef.current?.focus(), 0)
        } catch (error) {
            window.alert(error?.message || error || 'Party could not be saved')
        }
    }

    const handleEdit = (partyCode) => {
        const party = (parties || []).find((item) => item.PartyCode === partyCode)
        if (!party) return
        setEditingParty(party)
        setCateCode(party.PartyCategory || '')
        setOpeningStatus(Number(party.Debit) !== 0 || Number(party.Credit) !== 0)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const downloadTemplate = () => {
        const templateRows = [{
            PartyCategory: 'SUPPLIERS', PartyName: 'Sample Supplier', PartyUrduName: '', email: '',
            ContactNo: '03001234567', AltContactNo: '', PartyAddress: '', PartyUrduAddress: '',
            Debit: 0, Credit: 0, PartyStatus: true, ContactPerson: '', NTN: '', GST: ''
        }]
        const worksheet = XLSX.utils.json_to_sheet(templateRows)
        worksheet['!cols'] = Object.keys(templateRows[0]).map(() => ({ wch: 20 }))
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Parties')
        XLSX.writeFile(workbook, 'parties_import_template.xlsx')
    }

    const handleExcelImport = async (event) => {
        const file = event.target.files?.[0]
        if (!file) return
        setImporting(true)
        try {
            const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' })
            const worksheet = workbook.Sheets[workbook.SheetNames[0]]
            const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' })
            if (!rows.length) throw new Error('Excel file mein koi record nahi hai')
            const missingNameRow = rows.findIndex((row) => !String(row.PartyName || '').trim())
            if (missingNameRow !== -1) throw new Error(`Row ${missingNameRow + 2}: PartyName required hai`)
            const result = await dispatch(importParties(rows)).unwrap()
            await Promise.all([dispatch(getAllParties()), dispatch(getPartyCategories())])
            window.alert(result.message)
        } catch (error) {
            window.alert(error?.message || String(error))
        } finally {
            setImporting(false)
            event.target.value = ''
        }
    }

    const tableColumns = [
        { fieldName: 'PartyCode', cName: 'Party Code', textAlign: 'left', width: 80, status: 'en', dType: 'string' },
        { fieldName: 'PartyName', cName: 'Party Name', textAlign: 'left', width: 160, status: 'en', dType: 'string' },
        { fieldName: 'PartyAddress', cName: 'Party Address', textAlign: 'left', width: 220, status: 'en', dType: 'string' },
        { fieldName: 'ContactPerson', cName: 'Contact Person', textAlign: 'left', width: 130, status: 'en', dType: 'string' },
        { fieldName: 'ContactNo', cName: 'Contact No', textAlign: 'left', width: 120, status: 'en', dType: 'string' },
        { fieldName: 'Debit', cName: 'Debit', textAlign: 'right', width: 90, status: 'en', dType: 'number' },
        { fieldName: 'Credit', cName: 'Credit', textAlign: 'right', width: 90, status: 'en', dType: 'number' },
        { fieldName: 'Edit', cName: 'Edit', textAlign: 'center', width: 70, status: 'en', dType: 'string' },
        { fieldName: 'Delete', cName: 'Delete', textAlign: 'center', width: 70, status: 'dis', dType: 'string' },
        { fieldName: 'Action', cName: 'Action', textAlign: 'right', width: 80, status: 'dis', dType: 'string' }
    ]

    const renderField = (name, label, options = {}) => <TextField
        fullWidth size="small" name={name} id={name} label={label} inputRef={options.inputRef}
        type={options.type || 'text'} multiline={options.multiline} minRows={options.multiline ? 2 : undefined}
        defaultValue={editingParty ? (editingParty[name] ?? options.defaultValue ?? '') : (options.defaultValue ?? '')}
        inputProps={options.inputProps} sx={fieldSx}
    />

    const categories = partyCategories || []
    return <>
        <Navbar />
        <Box sx={{ minHeight: 'calc(100vh - 64px)', bgcolor: 'var(--web-theme-page-bg, #F5F7FB)', py: { xs: 2, md: 4 } }}>
            <Container maxWidth="xl">
                <Paper elevation={0} sx={{ mb: 3, p: { xs: 2.25, md: 3 }, borderRadius: 'var(--web-theme-radius, 12px)', color: 'var(--web-theme-topbar-text, #fff)', background: 'linear-gradient(135deg, var(--web-theme-topbar, #780202), var(--web-theme-primary, #A80707))' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
                        <Stack direction="row" spacing={2} alignItems="center"><Box sx={{ width: 50, height: 50, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,.15)' }}><BusinessCenterRoundedIcon /></Box><Box><Typography variant="h4" fontWeight={800}>Parties Information</Typography><Typography sx={{ opacity: .82 }}>Manage suppliers, customers and business contacts</Typography></Box></Stack>
                        <Chip label={`${(parties || []).length} records`} sx={{ bgcolor: 'rgba(255,255,255,.16)', color: 'inherit', fontWeight: 700, border: '1px solid rgba(255,255,255,.22)' }} />
                    </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ mb: 3, p: 2, borderRadius: 'var(--web-theme-radius, 12px)', borderColor: 'var(--web-theme-primary, #780202)', bgcolor: 'var(--web-theme-card-bg, #fff)' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2}>
                        <Box><Typography fontWeight={750}>Bulk Excel tools</Typography><Typography variant="body2" color="text.secondary">Download the template, enter category names and party details, then import.</Typography></Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}><Button variant="outlined" startIcon={<DownloadRoundedIcon />} onClick={downloadTemplate}>Download Template</Button><Button variant="contained" startIcon={<UploadFileRoundedIcon />} disabled={importing} onClick={() => excelInputRef.current.click()}>{importing ? 'Importing...' : 'Import Excel'}</Button><input ref={excelInputRef} type="file" accept=".xlsx,.xls" hidden onChange={handleExcelImport} /></Stack>
                    </Stack>
                </Paper>

                <Paper key={editingParty ? editingParty.PartyCode : 'new'} component="form" onSubmit={handleSubmit} elevation={0} variant="outlined" sx={{ mb: 3, p: { xs: 2, md: 3 }, borderRadius: 'var(--web-theme-radius, 12px)', bgcolor: 'var(--web-theme-card-bg, #fff)' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1} sx={{ mb: 2 }}><Box><Typography variant="h6" fontWeight={800}>{editingParty ? 'Edit Party' : 'Add New Party'}</Typography><Typography variant="body2" color="text.secondary">Fields are grouped to make detailed party entry easier.</Typography></Box>{editingParty && <Chip color="primary" variant="outlined" label={`Editing #${editingParty.PartyCode}`} />}</Stack>
                    <Divider sx={{ mb: 2.5 }} />

                    <Typography fontWeight={800} sx={{ mb: 1.5 }}>Basic Information</Typography>
                    <Grid2 container spacing={2}>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}><AutoComplete listData={categories} value={categories.find((c) => String(c.cateCode) === String(cateCode))} onChange={(data) => setCateCode(data?.cateCode || '')} /></Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>{renderField('PartyName', 'Party Name', { inputRef })}</Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>{renderField('PartyUrduName', 'Party Urdu Name')}</Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>{renderField('ContactPerson', 'Contact Person')}</Grid2>
                    </Grid2>

                    <Divider sx={{ my: 2.5 }} /><Typography fontWeight={800} sx={{ mb: 1.5 }}>Contact & Address</Typography>
                    <Grid2 container spacing={2}>
                        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>{renderField('email', 'Email', { type: 'email' })}</Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>{renderField('ContactNo', 'Contact No')}</Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>{renderField('AltContactNo', 'Alternate Contact No')}</Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>{renderField('PartyAddress', 'Party Address', { multiline: true })}</Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>{renderField('PartyUrduAddress', 'Party Urdu Address', { multiline: true })}</Grid2>
                    </Grid2>

                    <Divider sx={{ my: 2.5 }} /><Typography fontWeight={800} sx={{ mb: 1.5 }}>Tax & Opening Balance</Typography>
                    <Grid2 container spacing={2} alignItems="center">
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>{renderField('NTN', 'N.T.N #')}</Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>{renderField('GST', 'G.S.T #')}</Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}><FormControlLabel control={<Checkbox checked={openingStatus} onChange={(e) => setOpeningStatus(e.target.checked)} />} label="Add opening balance" /></Grid2>
                        {openingStatus && <><Grid2 size={{ xs: 12, sm: 6, md: 3 }}>{renderField('Debit', 'Debit', { type: 'number', defaultValue: 0 })}</Grid2><Grid2 size={{ xs: 12, sm: 6, md: 3 }}>{renderField('Credit', 'Credit', { type: 'number', defaultValue: 0 })}</Grid2></>}
                    </Grid2>

                    <Stack direction={{ xs: 'column-reverse', sm: 'row' }} justifyContent="flex-end" spacing={1.25} sx={{ mt: 3 }}>
                        {editingParty && <Button variant="outlined" color="inherit" startIcon={<CancelRoundedIcon />} onClick={(e) => resetForm(e.currentTarget.form)}>Cancel Edit</Button>}
                        <Button type="submit" variant="contained" size="large" startIcon={editingParty ? <SaveRoundedIcon /> : <AddRoundedIcon />} sx={{ minWidth: 180, borderRadius: 2, fontWeight: 750 }}>{editingParty ? 'Update Party' : 'Add Party'}</Button>
                    </Stack>
                </Paper>

                <Paper elevation={0} variant="outlined" sx={{ borderRadius: 'var(--web-theme-radius, 12px)', overflow: 'hidden', bgcolor: 'var(--web-theme-card-bg, #fff)' }}>
                    <Box sx={{ px: { xs: 2, md: 3 }, py: 2.25 }}><Stack direction="row" justifyContent="space-between" alignItems="center"><Box><Typography variant="h6" fontWeight={800}>Parties List</Typography><Typography variant="body2" color="text.secondary">Search, review and edit all available parties.</Typography></Box><Chip size="small" color="primary" variant="outlined" label={(parties || []).length} /></Stack></Box>
                    <Divider />
                    <Box sx={{ p: { xs: 1, md: 2 } }}><StickyHeadTable modernStyle tableColumns={tableColumns} tableData={parties || []} onEditClick={handleEdit} perPage={10} /></Box>
                </Paper>
            </Container>
        </Box>
    </>
}